/**
 * LinguistPro — Landing Page
 * Route: /
 * Light theme, teal accent, Tech Sight (Pty) Ltd
 *
 * Sections: Nav, Hero, Features, How It Works, Pricing, Footer
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HELP_URL, TERMS_URL, PRIVACY_URL } from '../lib/legalUrls';
import { useAuthStore } from '../store/useAuthStore';
import { paygFloorPriceNote, PAYG_RATE_TEXT } from '../services/paygFloor';

// ─── SVG Icons ───────────────────────────────────────────────────────────────

const IconMic = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
  </svg>
);

const IconGlobe = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const IconBrain = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m3.343-5.657-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const IconUpload = () => (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0-3 3m3-3v12" />
  </svg>
);

const IconCpu = () => (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
  </svg>
);

const IconDownload = () => (
  <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15l-3-3m0 0 3-3m-3 3h12M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
  </svg>
);

const IconCheck = () => (
  <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const IconLogo = () => (
  <svg className="w-7 h-7" fill="none" viewBox="0 0 32 32">
    <rect width="32" height="32" rx="8" fill="#00d4b8" />
    <path d="M8 11h16M8 16h10M8 21h13" stroke="#0d1117" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="24" cy="21" r="3" fill="#0d1117" />
  </svg>
);

// ─── Data ────────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: <IconMic />,
    title: 'Speaker Diarisation',
    body: 'Automatically identify and label each speaker. Track who said what — perfect for meetings, interviews, and legal proceedings.',
  },
  {
    icon: <IconGlobe />,
    title: 'Built for South Africa — Open to the World',
    body: '13 languages live — English, Afrikaans plus 11 global (Arabic, Chinese, French, German, Hindi, Italian, Japanese, Korean, Dutch, Portuguese, Spanish). Plus isiZulu, isiXhosa, Sesotho, Setswana, and Tshivenda in beta.',
  },
  {
    icon: <IconBrain />,
    title: 'AI Intelligence',
    body: 'Summaries, action items, and sentiment analysis layered on top of every transcript — available to all signed-in users.',
  },
];

const STEPS = [
  {
    icon: <IconUpload />,
    step: '01',
    title: 'Upload',
    body: 'Drop in any audio or video file. MP3, MP4, WAV, M4A — we handle them all, up to 100 MB per file.',
  },
  {
    icon: <IconCpu />,
    step: '02',
    title: 'AI Processes',
    body: 'Gemini 2.5 Flash and AssemblyAI transcribe with high accuracy. Speakers are separated. AI extracts the intelligence.',
  },
  {
    icon: <IconDownload />,
    step: '03',
    title: 'Download',
    body: 'Export as DOCX, SRT subtitles, or plain text. Share, archive, or drop straight into your workflow.',
  },
];

const FREE_FEATURES = [
  '30 minutes per month',
  '13 languages live + 5 SA languages in beta',
  'Speaker diarisation',
  'DOCX + SRT export',
];

const PAYG_FEATURES = [
  `${PAYG_RATE_TEXT} per hour — pay as you go`,
  '13 languages live + 5 SA languages in beta',
  'Speaker diarisation',
  'DOCX + SRT export',
  'No monthly commitment',
];

const PRO_FEATURES = [
  'Unlimited transcription',
  '13 languages live + 5 SA languages in beta',
  'Speaker diarisation',
  'DOCX + SRT export',
  'Priority support',
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function LandingPage() {
  const { session } = useAuthStore();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCTA = () => {
    if (session) {
      navigate('/app');
    } else {
      navigate('/auth?tab=signup');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans antialiased">

      {/* ── Nav ───────────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <IconLogo />
            <span className="text-lg font-bold tracking-tight">
              Linguist<span className="text-[#007a6d]">Pro</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-gray-900 transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Pricing</a>
            <a href={HELP_URL} rel="noopener" className="hover:text-gray-900 transition-colors">Help</a>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            {session ? (
              <Link
                to="/app"
                className="px-4 py-2 text-sm font-medium text-[#007a6d] border border-[#00d4b8]/40 rounded-lg hover:bg-[#00d4b8]/10 transition-colors"
              >
                Go to app →
              </Link>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/auth?tab=signup"
                  className="px-4 py-2 text-sm font-semibold bg-[#00d4b8] text-[#0d1117] rounded-lg hover:bg-[#00bfa8] transition-colors"
                >
                  Start free
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden p-2 text-gray-500 hover:text-gray-700"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white px-4 py-4 space-y-4 text-sm">
            <a href="#features" className="block text-gray-500 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#how-it-works" className="block text-gray-500 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>How it works</a>
            <a href="#pricing" className="block text-gray-500 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href={HELP_URL} rel="noopener" className="block text-gray-500 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Help</a>
            <div className="pt-2 flex flex-col gap-2">
              <Link to="/auth" className="block px-4 py-2 text-center text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Sign in</Link>
              <Link to="/auth?tab=signup" className="block px-4 py-2 text-center font-semibold bg-[#00d4b8] text-[#0d1117] rounded-lg hover:bg-[#00bfa8]">Start free</Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#00d4b8]/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-28 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 rounded-full border border-[#00d4b8]/30 bg-[#00d4b8]/5 text-[#007a6d] text-xs font-semibold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00d4b8] animate-pulse" />
            Built for South Africa
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            Transcribe.{' '}
            <span className="text-[#007a6d]">Analyse.</span>
            {' '}Know.
          </h1>

          {/* Subheadline */}
          <p className="max-w-2xl mx-auto text-lg sm:text-xl text-gray-500 leading-relaxed mb-10">
            South Africa's first self-serve transcription platform — 13 languages live
            plus Arabic, Chinese, French, German, Japanese, Spanish, and more, with five
            SA languages in beta. Speaker diarisation, AI intelligence, and high accuracy.
            Built for business.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleCTA}
              className="w-full sm:w-auto px-8 py-4 text-base font-bold bg-[#00d4b8] text-[#0d1117] rounded-xl hover:bg-[#00bfa8] transition-colors shadow-lg shadow-[#00d4b8]/20"
            >
              Start Free — 30 min on us
            </button>
            <a
              href="#pricing"
              className="w-full sm:w-auto px-8 py-4 text-base font-semibold text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
            >
              See Pricing
            </a>
          </div>

          {/* Social proof strip */}
          <p className="mt-8 text-xs text-gray-400">
            No credit card required · Billed in ZAR
          </p>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────────── */}
      <section id="features" className="py-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything you need</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Professional transcription tools that used to cost thousands — now available to any business, from R0.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {FEATURES.map(({ icon, title, body }) => (
              <div
                key={title}
                className="p-6 rounded-2xl bg-white border border-gray-200 shadow-sm hover:border-[#00d4b8]/30 hover:bg-[#00d4b8]/[0.03] transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00d4b8]/10 text-[#007a6d] flex items-center justify-center mb-5 group-hover:bg-[#00d4b8]/20 transition-colors">
                  {icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Three steps to clarity</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              From raw audio to actionable insights in minutes, not hours.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8 relative">
            {/* Connector line — desktop only */}
            <div className="hidden sm:block absolute top-10 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px bg-gradient-to-r from-transparent via-[#00d4b8]/30 to-transparent" />

            {STEPS.map(({ icon, step, title, body }) => (
              <div key={step} className="flex flex-col items-center text-center">
                <div className="relative w-20 h-20 rounded-2xl bg-[#00d4b8]/10 border border-[#00d4b8]/20 flex items-center justify-center text-[#007a6d] mb-5 z-10">
                  {icon}
                  <span className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-[#00d4b8] text-[#0d1117] text-[10px] font-black flex items-center justify-center">
                    {step}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Start free — upgrade when you need more. No hidden fees, billed in ZAR.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">

            {/* Free card */}
            <div className="p-7 rounded-2xl bg-white border border-gray-200 shadow-sm flex flex-col">
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Free</p>
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-extrabold text-gray-900">R0</span>
                  <span className="text-gray-400 text-sm mb-1">/month</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">No credit card needed</p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {FREE_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <span className="text-[#007a6d]"><IconCheck /></span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={handleCTA}
                className="w-full py-3 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                Get started free
              </button>
            </div>

            {/* PAYG card */}
            <div className="p-7 rounded-2xl bg-white border border-gray-200 shadow-sm flex flex-col">
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Pay-as-you-go</p>
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-extrabold text-gray-900">{PAYG_RATE_TEXT}</span>
                  <span className="text-gray-400 text-sm mb-1">/hr</span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Top up anytime. {paygFloorPriceNote()}</p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {PAYG_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <span className="text-[#007a6d]"><IconCheck /></span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={handleCTA}
                className="w-full py-3 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                Top up your balance
              </button>
            </div>

            {/* Pro card */}
            <div className="p-7 rounded-2xl bg-[#00d4b8]/5 border-2 border-[#00d4b8] flex flex-col relative">
              <div className="mb-6">
                <p className="text-sm font-semibold text-[#007a6d] uppercase tracking-wider mb-2">Pro</p>
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-extrabold text-gray-900">R299</span>
                  <span className="text-gray-500 text-sm mb-1">/month</span>
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  <span className="text-gray-700 font-medium">Unlimited transcription</span>
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {PRO_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="text-[#007a6d]"><IconCheck /></span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                disabled
                aria-disabled="true"
                className="w-full py-3 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold cursor-not-allowed"
              >
                Unlimited monthly plan, coming soon
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-200 bg-gray-100 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2.5">
            <IconLogo />
            <span>
              <span className="text-gray-600 font-semibold">LinguistPro</span>
              {' '}by Tech Sight (Pty) Ltd
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#features" className="hover:text-gray-600 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-600 transition-colors">Pricing</a>
            <a href={HELP_URL} rel="noopener" className="hover:text-gray-600 transition-colors">Help</a>
            <Link to="/support/new" className="hover:text-gray-600 transition-colors">Support</Link>
            <a href={PRIVACY_URL} rel="noopener" className="hover:text-gray-600 transition-colors">Privacy</a>
            <a href={TERMS_URL} rel="noopener" className="hover:text-gray-600 transition-colors">Terms</a>
            <Link to="/auth" className="hover:text-gray-600 transition-colors">Sign in</Link>
          </div>
          <p>© {new Date().getFullYear()} Tech Sight (Pty) Ltd. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
}
